<template>
  <section class="rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div class="flex flex-col gap-4 border-b border-gray-100 px-6 py-5 dark:border-gray-700 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">结算批次</h2>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">查看指定月份的历史批次与月结明细。</p>
      </div>
      <div class="w-full lg:w-64">
        <select
          :value="selectedTenantId ?? ''"
          class="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-indigo-900"
          @change="handleTenantChange"
        >
          <option value="">全部租户</option>
          <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">{{ tenant.name }}</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="flex min-h-[220px] items-center justify-center">
      <div class="text-center">
        <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
        <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载批次历史...</p>
      </div>
    </div>

    <div v-else-if="batches.length === 0" class="px-6 py-14 text-center">
      <p class="text-base font-medium text-gray-700 dark:text-gray-200">当前筛选条件下暂无结算批次</p>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">可先在“月结总览”中完成一笔结算，再回到这里查看历史记录。</p>
    </div>

    <div v-else class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-900/60">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">批次号</th>
            <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">租户</th>
            <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">月份</th>
            <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">记录数</th>
            <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">结算金额</th>
            <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">结算时间</th>
            <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">操作</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="batch in batches" :key="batch.id" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
            <td class="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">#{{ batch.id }}</td>
            <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{{ getTenantName(batch.tenantId) }}</td>
            <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{{ batch.settlementMonth.slice(0, 7) }}</td>
            <td class="px-4 py-4 text-right text-sm text-gray-500 dark:text-gray-400">{{ batch.recordCount }}</td>
            <td class="px-4 py-4 text-right text-sm font-medium text-emerald-600 dark:text-emerald-400">{{ formatCurrency(batch.tenantAmount) }}</td>
            <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{{ formatDateTime(batch.confirmedAt) }}</td>
            <td class="px-4 py-4 text-right text-sm">
              <button
                type="button"
                class="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                @click="emit('view-detail', batch.id)"
              >
                详情
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { AdminTenantConfig, TenantSettlementBatch } from '@/types/admin/tenant'
import { formatCurrency, formatDateTime } from '@/utils/formatters'

interface Props {
  loading: boolean
  batches: TenantSettlementBatch[]
  tenants: AdminTenantConfig[]
  selectedTenantId: number | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:selectedTenantId': [value: number | null]
  'view-detail': [batchId: number]
}>()

/**
 * 获取租户名称。
 */
const getTenantName = (tenantId: number) => {
  return props.tenants.find((tenant) => tenant.id === tenantId)?.name || `租户 #${tenantId}`
}

/**
 * 处理租户筛选切换。
 */
const handleTenantChange = (event: Event) => {
  const value = (event.target as HTMLSelectElement).value
  emit('update:selectedTenantId', value ? Number(value) : null)
}
</script>
